import * as React from "react";
import { handleEvent, handleEventImmediately } from "util/user-events";
import { isWebGLSupported } from "./map-error-boundary";
import { SELF_HOSTED_SOURCE_ID } from "./map-style";
import type { Basemap, BasemapState, MapStyleResult } from "./use-map-style";

export type MapSurface =
    | "community-search"
    | "client-map-view"
    | "agent-directory"
    | "reference-organizations"
    | "reference-contacts"
    | "community-detail"
    | "organization-detail"
    | "contact-detail"
    | "community-comparison"
    | "guest-comparison";

export interface MapLoadEvent {
    target: { getContainer: () => { clientWidth: number; clientHeight: number } };
}

export interface MapErrorEvent {
    error: Error;
    sourceId?: string;
}

export interface MapLoadTracking {
    onMapMounted: () => void;
    onLoad: (event: MapLoadEvent) => void;
    onError: (event: MapErrorEvent) => void;
}

type MapLoadOutcome =
    | "loaded"
    | "loadedWithErrors"
    | "failed"
    | "abandoned"
    | "notRendered"
    | "stuckLoading"
    | "unavailable";

type AttemptedBasemap = Basemap | "none";

type Deliver = typeof handleEventImmediately;

interface ErrorDetails {
    errorBasemap: Basemap | null;
    errorSourceId: string | null;
    errorStatus: number | null;
    errorHost: string | null;
    errorName: string | null;
    errorMessage: string | null;
}

const NO_ERROR: ErrorDetails = {
    errorBasemap: null,
    errorSourceId: null,
    errorStatus: null,
    errorHost: null,
    errorName: null,
    errorMessage: null,
};

const PMTILES_BAD_RESPONSE = /^Bad response code: (\d+)$/;

const URL_WITH_ANY_SCHEME = /(?:[a-z][a-z\d+.-]*:\/\/)+([^\s/?#]+)\S*/gi;

const attemptedBasemapOf = (result: MapStyleResult): AttemptedBasemap => {
    if (result.status !== "ready") {
        return "none";
    }
    return typeof result.style === "string" ? "mapTiler" : "selfHosted";
};

const errorBasemapOf = (
    sourceId: string | null,
    attempted: AttemptedBasemap,
): Basemap | null => {
    if (sourceId !== null) {
        return sourceId === SELF_HOSTED_SOURCE_ID ? "selfHosted" : "mapTiler";
    }
    return attempted === "none" ? null : attempted;
};

const statusOf = (error: Error): number | null => {
    const status = (error as { status?: unknown }).status;
    if (typeof status === "number") {
        return status;
    }
    const pmtilesMatch = PMTILES_BAD_RESPONSE.exec(error.message);
    return pmtilesMatch ? Number(pmtilesMatch[1]) : null;
};

const hostOf = (error: Error): string | null => {
    const url = (error as { url?: unknown }).url;
    if (typeof url !== "string") {
        return null;
    }
    try {
        return new URL(url).host;
    } catch {
        return null;
    }
};

const withoutUrls = (message: string): string =>
    message.replace(URL_WITH_ANY_SCHEME, (_match, host: string) => host);

const errorDetailsOf = (
    event: MapErrorEvent,
    attempted: AttemptedBasemap,
): ErrorDetails => {
    const sourceId = typeof event.sourceId === "string" ? event.sourceId : null;
    return {
        errorBasemap: errorBasemapOf(sourceId, attempted),
        errorSourceId: sourceId,
        errorStatus: statusOf(event.error),
        errorHost: hostOf(event.error),
        errorName: event.error.name,
        errorMessage: withoutUrls(event.error.message),
    };
};

const isContainerVisible = (event: MapLoadEvent): boolean => {
    const container = event.target.getContainer();
    return container.clientWidth > 0 && container.clientHeight > 0;
};

const isLoadedOutcome = (outcome: MapLoadOutcome): boolean =>
    outcome === "loaded" || outcome === "loadedWithErrors";

const summaryFor = (
    requested: Basemap,
    rendered: AttemptedBasemap,
    outcome: MapLoadOutcome,
    unavailableReason: string | null,
): string => {
    switch (outcome) {
        case "loaded":
            return `wanted-${requested}-got-${rendered}`;
        case "loadedWithErrors":
            return `wanted-${requested}-got-${rendered}-with-errors`;
        case "notRendered":
            return `wanted-${requested}-map-not-rendered`;
        case "unavailable":
            return `wanted-${requested}-got-nothing-unavailable-${unavailableReason}`;
        case "failed":
        case "abandoned":
        case "stuckLoading":
            return `wanted-${requested}-got-nothing-${outcome}`;
        default: {
            const exhaustiveCheck: never = outcome;
            throw new Error(`Unhandled map load outcome ${exhaustiveCheck}`);
        }
    }
};

const readyOutcomeOf = (
    mapMountedAt: number | null,
    errorCount: number,
): MapLoadOutcome => {
    if (mapMountedAt === null) {
        return "notRendered";
    }
    return errorCount > 0 ? "failed" : "abandoned";
};

const unloadedOutcomeOf = (
    result: MapStyleResult,
    mapMountedAt: number | null,
    errorCount: number,
): MapLoadOutcome => {
    switch (result.status) {
        case "loading":
            return "stuckLoading";
        case "unavailable":
            return "unavailable";
        case "ready":
            return readyOutcomeOf(mapMountedAt, errorCount);
        default: {
            const exhaustiveCheck: never = result;
            throw new Error(
                `Unhandled map style result ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

class MapLoadTracker implements MapLoadTracking {
    private readonly mapLoadId = crypto.randomUUID();
    private readonly startedAt = performance.now();
    private mapMountedAt: number | null = null;
    private loadedAt: number | null = null;
    private mapVisible: boolean | null = null;
    private loadReported = false;
    private postLoadErrorReported = false;
    private errorCount = 0;
    private firstError: ErrorDetails = NO_ERROR;

    constructor(
        private surface: MapSurface,
        private basemap: BasemapState,
    ) {}

    update(surface: MapSurface, basemap: BasemapState): void {
        this.surface = surface;
        this.basemap = basemap;
    }

    onMapMounted = (): void => {
        if (this.mapMountedAt === null) {
            this.mapMountedAt = performance.now();
        }
    };

    onLoad = (event: MapLoadEvent): void => {
        if (this.loadReported) {
            return;
        }
        this.loadedAt = performance.now();
        this.mapVisible = isContainerVisible(event);
        this.reportLoad(handleEvent, this.errorCount > 0 ? "loadedWithErrors" : "loaded");
    };

    onError = (event: MapErrorEvent): void => {
        console.error(event.error);
        const details = errorDetailsOf(event, attemptedBasemapOf(this.basemap.result));
        if (this.loadedAt === null) {
            this.recordPreLoadError(details);
            return;
        }
        this.reportPostLoadError(details, this.loadedAt);
    };

    leave(deliver: Deliver): void {
        if (this.loadReported) {
            return;
        }
        const { result } = this.basemap;
        this.reportLoad(
            deliver,
            unloadedOutcomeOf(result, this.mapMountedAt, this.errorCount),
        );
    }

    resume(): void {
        if (this.loadedAt === null) {
            this.loadReported = false;
        }
    }

    private recordPreLoadError(details: ErrorDetails): void {
        if (this.errorCount === 0) {
            this.firstError = details;
        }
        this.errorCount += 1;
    }

    private reportPostLoadError(details: ErrorDetails, loadedAt: number): void {
        if (this.postLoadErrorReported) {
            return;
        }
        this.postLoadErrorReported = true;
        handleEvent("map-error", {
            ...this.requestContext(),
            renderedBasemap: attemptedBasemapOf(this.basemap.result),
            ...details,
            msSinceLoad: Math.round(performance.now() - loadedAt),
        });
    }

    private reportLoad(deliver: Deliver, outcome: MapLoadOutcome): void {
        this.loadReported = true;
        const { result, requestedBasemap, manifestStatus } = this.basemap;
        const attempted = attemptedBasemapOf(result);
        const rendered = isLoadedOutcome(outcome) ? attempted : "none";
        const unavailableReason = result.status === "unavailable" ? result.reason : null;
        deliver("map-load", {
            ...this.requestContext(),
            attemptedBasemap: attempted,
            renderedBasemap: rendered,
            outcome,
            summary: summaryFor(requestedBasemap, rendered, outcome, unavailableReason),
            manifestStatus,
            unavailableReason,
            errorCount: this.errorCount,
            ...this.firstError,
            webglSupported: isWebGLSupported(),
            mapVisible: this.mapVisible,
            ...this.timings(),
        });
    }

    private timings() {
        if (this.mapMountedAt === null) {
            return { msBeforeMapMounted: null, durationMs: null };
        }
        return {
            msBeforeMapMounted: Math.round(this.mapMountedAt - this.startedAt),
            durationMs: Math.round(performance.now() - this.mapMountedAt),
        };
    }

    private requestContext() {
        return {
            mapLoadId: this.mapLoadId,
            surface: this.surface,
            policy: this.basemap.policy,
            requestedBasemap: this.basemap.requestedBasemap,
        };
    }
}

export const useMapLoadTracking = (
    surface: MapSurface,
    basemap: BasemapState,
): MapLoadTracking => {
    const [tracker] = React.useState(() => new MapLoadTracker(surface, basemap));
    React.useEffect(() => {
        tracker.update(surface, basemap);
    }, [tracker, surface, basemap]);
    React.useEffect(() => {
        const onPageHide = () => tracker.leave(handleEventImmediately);
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                tracker.resume();
            }
        };
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);
        return () => {
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
            tracker.leave(handleEvent);
        };
    }, [tracker]);
    return tracker;
};

export const MapMountTracker: React.FC<{ tracking: MapLoadTracking }> = ({
    tracking,
}) => {
    React.useEffect(() => {
        tracking.onMapMounted();
    }, [tracking]);
    return null;
};
